
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/auth"; // Updated import
import { createOrganization } from "@/lib/supabase";

const OrganizationSetup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orgName, setOrgName] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("standard");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Si l'utilisateur n'est pas connecté, rediriger vers la page de connexion
  if (!user) {
    navigate("/login");
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!orgName.trim()) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer un nom d'organisation valide",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Création de l'organisation en utilisant la fonction du fichier supabase.ts
      const response = await createOrganization(user.id, orgName, selectedPlan);
      
      if (!response.success) {
        throw new Error(response.error || "Erreur inconnue lors de la création de l'organisation");
      }
      
      toast({
        title: "Organisation créée",
        description: "Votre organisation a été créée avec succès",
      });
      
      // Redirection vers le tableau de bord
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Erreur lors de la création de l'organisation:", error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de la création de l'organisation",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Configurer votre organisation</h1>
          <p className="mt-2 text-gray-600">
            Créez votre organisation pour commencer à utiliser SocialSync
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="mt-8 space-y-6 bg-white p-6 shadow-md rounded-lg">
          <div className="space-y-4">
            <div>
              <Label htmlFor="orgName">Nom de l'organisation</Label>
              <Input
                id="orgName"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Ex: Ma Société"
                required
              />
            </div>
            
            <div className="space-y-4">
              <Label>Sélectionnez un plan</Label>
              <RadioGroup value={selectedPlan} onValueChange={setSelectedPlan}>
                <div className="flex items-center space-x-2 border p-4 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="standard" id="standard" />
                  <Label htmlFor="standard" className="flex-1 cursor-pointer">
                    <div className="font-medium">Standard</div>
                    <div className="text-sm text-gray-500">Pour les petites équipes</div>
                  </Label>
                  <div className="font-medium">79€/mois</div>
                </div>
                
                <div className="flex items-center space-x-2 border p-4 rounded-lg hover:bg-gray-50 bg-blue-50 border-blue-200">
                  <RadioGroupItem value="professional" id="professional" />
                  <Label htmlFor="professional" className="flex-1 cursor-pointer">
                    <div className="font-medium">Professionnel</div>
                    <div className="text-sm text-gray-500">Pour les équipes en croissance</div>
                  </Label>
                  <div className="font-medium">119€/mois</div>
                </div>
                
                <div className="flex items-center space-x-2 border p-4 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="enterprise" id="enterprise" />
                  <Label htmlFor="enterprise" className="flex-1 cursor-pointer">
                    <div className="font-medium">Entreprise</div>
                    <div className="text-sm text-gray-500">Pour les grandes organisations</div>
                  </Label>
                  <div className="font-medium">149€/mois</div>
                </div>
              </RadioGroup>
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            {isSubmitting ? "Création en cours..." : "Créer mon organisation"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default OrganizationSetup;
